/**
 * Focus management and scroll utilities.
 * Provides safe element focusing and scroll-into-view functionality.
 */

'use strict';

/**
 * Focus an element by its ID.
 * 
 * @param id - Element ID
 * @returns True if element was found and focused
 */
export function focusById(id: string): boolean {
    try {
        const element = document.getElementById(id);
        if (element && typeof element.focus === 'function') {
            element.focus();
            return true;
        }
        return false;
    } catch {
        return false;
    }
}

/**
 * Focus the first element matching a CSS selector.
 * 
 * @param selector - CSS selector
 * @returns True if element was found and focused
 */
export function focusBySelector(selector: string): boolean {
    try {
        const element = document.querySelector(selector);
        if (element && typeof (element as HTMLElement).focus === 'function') {
            (element as HTMLElement).focus();
            return true;
        }
        return false;
    } catch {
        return false;
    }
}

/**
 * Scroll an element into view by its ID.
 * 
 * @param id - Element ID
 * @param behavior - Scroll behavior ('auto', 'smooth', or 'instant')
 * @returns True if element was found and scrolled
 */
export function scrollIntoViewById(
    id: string,
    behavior?: ScrollBehavior
): boolean {
    try {
        const element = document.getElementById(id);
        if (!element) {
            return false;
        }

        element.scrollIntoView({
            behavior: behavior || 'auto',
            block: 'nearest',
            inline: 'nearest'
        });

        return true;
    } catch {
        return false;
    }
}
